import type {
  OpeningChannelResult,
  OpeningResult,
  PostingResultData,
  PostingResultScenario,
} from '../types';
import type {
  SourcingPostingBatch,
  SourcingPostingItem,
} from 'api-clients/monolith';

/**
 * Display order of a batch's openings: openings with a failed row first, then
 * by their first row's `position`. The page computes it once per mount so
 * rows do not jump as they settle.
 */
export function postingBatchOpeningOrder(
  batch: SourcingPostingBatch,
): string[] {
  const openings = new Map<string, { failed: boolean; position: number }>();
  for (const item of batch.items) {
    const entry = openings.get(item.opening.id);
    const failed = item.state === 'failed';
    if (entry) {
      entry.failed ||= failed;
      entry.position = Math.min(entry.position, item.position);
    } else {
      openings.set(item.opening.id, { failed, position: item.position });
    }
  }
  return [...openings.entries()]
    .sort(
      ([, a], [, b]) =>
        Number(b.failed) - Number(a.failed) || a.position - b.position,
    )
    .map(([id]) => id);
}

const toChannelResult = (item: SourcingPostingItem): OpeningChannelResult => {
  const name = item.channel.name ?? item.channel.slug;
  return {
    channelSlug: item.channel.slug,
    channelName: name,
    displayTitle: name,
    logoSquareUrl: item.channel.logo_url,
    logoRectangleUrl: null,
    status: item.state,
    budget: item.budget ?? 0,
    itemId: item.id,
    attempt: item.attempt,
    reasonCode: item.reason_code,
    reasonMessage: item.reason_message,
    retryable: item.retryable,
    note: item.note,
    paid: item.channel.paid,
    processingTimeHours: item.processing_time_hours,
  };
};

/**
 * Groups a batch's items into openings in `openingOrder`, with openings the
 * order does not name after it in position order. Spend counts posted rows.
 */
export function mapPostingBatch(
  batch: SourcingPostingBatch,
  openingOrder: string[] = postingBatchOpeningOrder(batch),
): PostingResultData {
  const byOpening = new Map<string, OpeningResult>();
  for (const item of [...batch.items].sort((a, b) => a.position - b.position)) {
    const opening = byOpening.get(item.opening.id) ?? {
      openingId: item.opening.id,
      openingTitle: item.opening.title,
      channels: [],
    };
    opening.channels.push(toChannelResult(item));
    byOpening.set(item.opening.id, opening);
  }

  const rank = new Map(openingOrder.map((id, index) => [id, index]));
  const unranked = openingOrder.length;
  const openings = [...byOpening.values()].sort(
    (a, b) =>
      (rank.get(a.openingId) ?? unranked) - (rank.get(b.openingId) ?? unranked),
  );

  // Once every row settled, a queued or posting row is a Retry, which only a
  // failed row allows. The summary counts it as failed until it settles again.
  const isRetrying = batch.settled >= batch.total;
  const isFailed = (c: OpeningChannelResult) =>
    c.status === 'failed' ||
    (isRetrying && (c.status === 'queued' || c.status === 'posting'));

  const rows = openings.flatMap(o => o.channels);
  const posted = rows.filter(c => c.status === 'posted');
  const failed = rows.filter(isFailed);
  // A first-run queued or posting row is neither a success nor a failure yet,
  // so only a batch whose every row settled the same way gets an all-* scenario.
  let scenario: PostingResultScenario = 'partial';
  if (posted.length === rows.length) scenario = 'all-success';
  else if (failed.length === rows.length) scenario = 'all-failed';

  return {
    scenario,
    summary: {
      openingsSubmitted: openings.length,
      postedOpenings: openings.filter(o =>
        o.channels.every(c => c.status === 'posted'),
      ).length,
      failedOpenings: openings.filter(o => o.channels.some(isFailed)).length,
      channels: new Set(rows.map(c => c.channelSlug)).size,
      totalSpend: posted.reduce((sum, c) => sum + c.budget, 0),
    },
    openings,
  };
}
